import { apiClient } from "../../apiClient";
import { ApiResponse } from "../../../types/api";
import {
  Customer,
  CustomerApiResponse,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  GetCustomersParams,
  GetCustomersResponse,
  CreateCustomerFormData,
  CustomerDetailResponse,
} from "../../../types/customer";
import { Country } from "../countries/type";

class CustomerService {
  private countriesCache: Country[] | null = null;

  async getCountries(): Promise<Country[]> {
    if (this.countriesCache) {
      return this.countriesCache;
    }

    try {
      const response = await apiClient.get<ApiResponse<Country[]>>(
        "/countries/?skip=0&limit=100",
      );
      this.countriesCache = response.data.data;
      return this.countriesCache;
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      return [];
    }
  }

  private async transformCustomer(
    apiCustomer: CustomerApiResponse,
  ): Promise<Customer> {
    const countries = await this.getCountries();
    const country = countries.find((c) => c.id === apiCustomer.countryId);

    return {
      ...apiCustomer,
      country: country?.name || "Unknown",
      avatar: apiCustomer.photo,
      banStatus: apiCustomer.banStatus || "unknown",
      banEndDate: apiCustomer.banEndDate || null,
      banRemarks: apiCustomer.banRemarks || null,
    };
  }

  async getCustomers(
    params: GetCustomersParams = {},
  ): Promise<GetCustomersResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.skip !== undefined)
        queryParams.append("skip", params.skip.toString());
      if (params.limit !== undefined)
        queryParams.append("limit", params.limit.toString());
      if (params.name) queryParams.append("name", params.name);
      if (params.customerType)
        queryParams.append("customerType", params.customerType);
      if (params.ban_status)
        queryParams.append("ban_status", params.ban_status);

      const response = await apiClient.get<ApiResponse<GetCustomersResponse>>(
        `/customers/?${queryParams.toString()}`,
      );

      const customers = await Promise.all(
        response.data.data.items.map((customer: CustomerApiResponse) =>
          this.transformCustomer(customer),
        ),
      );

      return {
        items: customers,
        pagination: response.data.data.pagination,
        filtersApplied: response.data.data.filtersApplied,
      };
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      throw error;
    }
  }

  async getCustomer(id: string): Promise<CustomerDetailResponse> {
    try {
      const response = await apiClient.get<ApiResponse<CustomerDetailResponse>>(
        `/customers/${id}`,
      );

      const transformedCustomer = await this.transformCustomer(
        response.data.data.customer,
      );
      return {
        customer: transformedCustomer,
        financials: response.data.data.financials,
        lastEntry: response.data.data.lastEntry,
      };
    } catch (error) {
      console.error(`Failed to fetch customer ${id}:`, error);
      throw error;
    }
  }

  async getCustomerBasicInfo(id: string): Promise<Customer> {
    try {
      const response = await apiClient.get<ApiResponse<CustomerDetailResponse>>(
        `/customers/${id}`,
      );

      // Return customer without country transformation to avoid unnecessary API call
      const customer = response.data.data.customer;
      return {
        ...customer,
        country: "N/A", // We don't need country info in agent details
        avatar: customer.photo,
        banStatus: customer.banStatus || "unknown",
        banEndDate: customer.banEndDate || null,
        banRemarks: customer.banRemarks || null,
      };
    } catch (error) {
      console.error(`Failed to fetch customer ${id}:`, error);
      throw error;
    }
  }

  async createCustomer(data: CreateCustomerRequest): Promise<Customer> {
    try {
      const response = await apiClient.post<ApiResponse<CustomerApiResponse>>(
        "/customers/",
        data,
      );

      return await this.transformCustomer(response.data.data);
    } catch (error) {
      console.error("Failed to create customer:", error);
      throw error;
    }
  }

  async createCustomerWithFiles(
    data: CreateCustomerFormData,
  ): Promise<Customer> {
    try {
      const formData = new FormData();

      formData.append("name", data.name);
      if (data.email) {
        formData.append("email", data.email);
      }
      formData.append("phone", data.phone || "");
      formData.append("royalty", data.royalty?.toString() || "0");
      formData.append("customerType", data.customerType || "regular");
      formData.append("countryId", data.countryId);
      formData.append("dateOfBirth", data.dateOfBirth || "");

      if (data.stateId) {
        formData.append("stateId", data.stateId);
      }

      if (data.playerId) {
        formData.append("playerId", data.playerId.toString());
      }

      if (data.photo && data.photo instanceof File) {
        formData.append("photo", data.photo, data.photo.name);
      }

      if (data.requiredDocs && data.requiredDocs instanceof File) {
        formData.append(
          "requiredDocs",
          data.requiredDocs,
          data.requiredDocs.name,
        );
      }

      if (data.requiredDocsType) {
        formData.append("requiredDocsType", data.requiredDocsType);
      }

      if (data.requiredDocsExpiresAt) {
        formData.append("requiredDocsExpiresAt", data.requiredDocsExpiresAt);
      }

      if (data.requiredDocsNumber) {
        formData.append("requiredDocsNumber", data.requiredDocsNumber);
      }

      if (data.requiredDocs) {
        formData.append("requiredDocsIsRequired", "true");
      }

      if (data.optionalDocs && data.optionalDocs instanceof File) {
        formData.append(
          "optionalDocs",
          data.optionalDocs,
          data.optionalDocs.name,
        );
      }

      if (data.optionalDocsType) {
        formData.append("optionalDocsType", data.optionalDocsType);
      }

      if (data.optionalDocsExpiresAt) {
        formData.append("optionalDocsExpiresAt", data.optionalDocsExpiresAt);
      }

      if (data.optionalDocsNumber) {
        formData.append("optionalDocsNumber", data.optionalDocsNumber);
      }

      if (data.optionalDocs) {
        formData.append("optionalDocsIsRequired", "false");
      }

      if (data.customerCardNumber !== undefined) {
        formData.append("customerCardNumber", data.customerCardNumber || "");
      }

      const response = await apiClient.post<ApiResponse<CustomerApiResponse>>(
        "/customers/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return await this.transformCustomer(response.data.data);
    } catch (error) {
      console.error("Failed to create customer with files:", error);
      throw error;
    }
  }

  async updateCustomer(
    customerId: string,
    data: UpdateCustomerRequest,
  ): Promise<Customer> {
    const formData = new FormData();

    if (data.name) formData.append("name", data.name);
    if (data.email) formData.append("email", data.email);
    if (data.phone) formData.append("phone", data.phone);
    if (data.royalty !== undefined)
      formData.append("royalty", data.royalty.toString());
    if (data.customerType) formData.append("customerType", data.customerType);
    if (data.countryId) formData.append("countryId", data.countryId);
    if (data.dateOfBirth) formData.append("dateOfBirth", data.dateOfBirth);

    if (data.stateId) {
      formData.append("stateId", data.stateId);
    }

    if (data.customerCardNumber !== undefined) {
      formData.append("customerCardNumber", data.customerCardNumber || "");
    }

    try {
      const response = await apiClient.put<ApiResponse<CustomerApiResponse>>(
        `/customers/${customerId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return await this.transformCustomer(response.data.data);
    } catch (error) {
      console.error(
        `Failed to update customer (multipart) ${customerId}:`,
        error,
      );
      throw error;
    }
  }

  async updateCustomerPhoto(
    customerId: string,
    photoFile: File,
  ): Promise<Customer> {
    const formData = new FormData();
    formData.append("photo", photoFile);

    try {
      const response = await apiClient.put<ApiResponse<CustomerApiResponse>>(
        `/customers/${customerId}/photo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return await this.transformCustomer(response.data.data);
    } catch (error) {
      console.error(`Failed to update customer photo ${customerId}:`, error);
      throw error;
    }
  }

  async deleteCustomer(id: string): Promise<void> {
    try {
      await apiClient.delete(`/customers/${id}`);
    } catch (error) {
      console.error(`Failed to delete customer ${id}:`, error);
      throw error;
    }
  }

  getCustomerTypes(): Array<{
    value: "regular" | "vip" | "vvip";
    label: string;
  }> {
    return [
      { value: "regular", label: "Regular" },
      { value: "vip", label: "VIP" },
      { value: "vvip", label: "VVIP" },
    ];
  }

  clearCountriesCache(): void {
    this.countriesCache = null;
  }
}

export const customerService = new CustomerService();

export type {
  GetCustomersParams,
  GetCustomersResponse,
  CreateCustomerRequest,
  UpdateCustomerRequest,
};
